import { useEffect } from 'react';

/**
 * useJsonLd — atomic management of a single <script type="application/ld+json"> tag.
 *
 * Why this exists:
 *   `react-helmet@6` has a known bug: when a JSON-LD <script> tag rendered inside
 *   <Helmet> has its content replaced (e.g., from an async fetch updating state),
 *   the previous <script> stays in the DOM and a new one is appended alongside it.
 *   Result: duplicate FAQPage / AboutPage / etc. schemas, which Google Search
 *   Console flags as "Duplicate field FAQPage" and refuses to validate.
 *
 *   This hook bypasses Helmet entirely for the JSON-LD payload. It guarantees that
 *   exactly one <script> with the given id exists in <head> at any time, and that
 *   it is removed cleanly on unmount.
 *
 * Usage:
 *   useJsonLd('faq-schema', faqSchema);
 *   useJsonLd('about-schema', aboutSchema);
 *
 * Pass `null` or `undefined` as data to skip injection (and clean up any prior tag).
 */
export function useJsonLd(id, data) {
  // Stringify outside the effect so the dependency stays a stable primitive
  const serialized = data ? JSON.stringify(data) : null;

  useEffect(() => {
    if (!id) return undefined;

    // Always remove any prior tag with this id before re-inserting
    const existing = document.getElementById(id);
    if (existing) existing.remove();

    if (!serialized) return undefined;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    script.textContent = serialized;
    document.head.appendChild(script);

    return () => {
      const tag = document.getElementById(id);
      if (tag) tag.remove();
    };
  }, [id, serialized]);
}
